import React from 'react';
import { 
  Calendar, 
  MapPin, 
  Users, 
  CreditCard, 
  CheckCircle, 
  Clock, 
  AlertCircle,
  ExternalLink,
  Phone,
  Mail,
  Bus,
  IndianRupee,
  Download,
  Send
} from 'lucide-react';
import { Message, BookingsDocument } from '../../types';
import { generateBookingReceipt, sendBookingReceipt } from '../../utils/pdfReceipt';
import { apiService } from '../../services/api';
import { useChatStore } from '../../stores/chatStore';
import toast from 'react-hot-toast';

interface BookingCardProps {
  booking: Message['any'];
}

export const BookingCard: React.FC<BookingCardProps> = ({ booking }) => {
  const { sendMessage } = useChatStore();
  
  if (!booking) return null;

  const getStatusColor = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'PAID':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'INIT':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'PENDING':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'CANCELLED':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'REFUNDED':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'PAID':
        return <CheckCircle className="h-4 w-4" />;
      case 'INIT':
        return <Clock className="h-4 w-4" />;
      case 'PENDING':
        return <AlertCircle className="h-4 w-4" />;
      case 'CANCELLED':
        return <AlertCircle className="h-4 w-4" />;
      case 'REFUNDED':
        return <CheckCircle className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const getGenderText = (gender: number) => {
    return gender === 1 ? 'Male' : gender === 0 ? 'Female' : 'Other';
  };

  const canDownloadReceipt = () => {
    const status = booking.status?.toUpperCase();
    return status === 'PAID' || status === 'PARTIALLYPAID' || status === 'PARTIALLY_PAID';
  };

  const convertToBookingsDocument = (): BookingsDocument => {
    return {
      id: booking.id || '',
      slotId: booking.slotId || '',
      inventoryId: booking.inventoryId || '',
      couponCode: booking.couponCode,
      userId: booking.userId || 0,
      userName: booking.userName || '',
      phoneNumber: booking.phoneNumber || '',
      email: booking.email,
      actualAmount: booking.actualAmount || 0,
      ticketAmount: booking.ticketAmount || 0,
      orderAmount: booking.orderAmount || 0,
      amountPaid: booking.amountPaid || 0,
      totalDiscount: booking.totalDiscount || 0,
      createdOn: booking.createdOn || Date.now(),
      modifiedOn: booking.modifiedOn || Date.now(),
      status: booking.status || 'init',
      tripDate: booking.tripDate || '',
      startTime: booking.startTime,
      eventImage: booking.eventImage,
      eventName: booking.eventName || '',
      convenienceFee: booking.convenienceFee || 0,
      count: booking.count || 0,
      travellers: booking.travellers || [],
      partialPaymentAlertShown: booking.partialPaymentAlertShown,
      kidAlertShown: booking.kidAlertShown,
      hasKids: booking.hasKids,
      remark: booking.remark,
      deleted: booking.deleted || false,
      remainingAmountLink: booking.remainingAmountLink,
      successfulPaymentCount: booking.successfulPaymentCount || 0,
      searchIndex: booking.searchIndex || '',
      source: booking.source,
      amountRemaining: booking.amountRemaining || 0,
      npsIdCreated: booking.npsIdCreated || false,
      whatsAppSmsSent: booking.whatsAppSmsSent || false,
      gateway: booking.gateway,
      gstAmount: booking.gstAmount,
      subText: booking.subText,
      driveLink: booking.driveLink,
      canEdit: booking.canEdit || false,
      canReschedule: booking.canReschedule || false,
      bookingRefId: booking.bookingRefId,
      paymentReminderTime: booking.paymentReminderTime,
      paymentCount: booking.paymentCount || 0
    };
  };

  const handleDownloadReceipt = async () => {
    try {
      const bookingData = convertToBookingsDocument();
      await generateBookingReceipt(bookingData);
    } catch (error) {
      console.error('Failed to generate receipt:', error);
      toast.error('Failed to download receipt');
    }
  };

  const handleSendReceipt = async () => {
    if (!booking.userId) {
      toast.error('User ID not available');
      return;
    }

    try {
      toast.loading('Generating and sending receipt...');
      const bookingData = convertToBookingsDocument();

      await sendBookingReceipt(
        booking.userId,
        bookingData,
        apiService.uploadMedia.bind(apiService),
        async (userId: number, messageData: { message: string; type: string }) => {
          await sendMessage(userId, {
            message: messageData.message,
            type: messageData.type
          });
        }
      );

      toast.dismiss();
      toast.success('Receipt sent successfully!');
    } catch (error: any) {
      console.error('Failed to send receipt:', error);
      toast.dismiss();
      toast.error(error.message || 'Failed to send receipt');
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden max-w-md mx-auto hover:shadow-xl transition-shadow duration-200">
      {/* Header with Event Image */}
      <div className="relative">
        {booking.eventImage && (
          <img
            src={booking.eventImage}
            alt={booking.eventName || 'Event'}
            className="w-full h-32 object-cover"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
            }}
          />
        )}
        <div className="absolute top-3 right-3">
          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border backdrop-blur-sm bg-white/90 ${getStatusColor(booking.status || '')}`}>
            {getStatusIcon(booking.status || '')}
            <span className="ml-1">{booking.status || 'Unknown'}</span>
          </span>
        </div>
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/50 to-transparent h-8"></div>
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Event Name */}
        <h3 className="text-lg font-semibold text-gray-900 mb-2 overflow-hidden" style={{display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical'}}>
          {booking.eventName || 'Event'}
        </h3>

        {/* Trip Details */}
        <div className="space-y-2 mb-4">
          <div className="flex items-center text-sm text-gray-600">
            <Calendar className="h-4 w-4 mr-2 text-blue-500" />
            <span>{booking.tripDate || 'Date not specified'}</span>
          </div>
          
          {booking.travellers && booking.travellers.length > 0 && (
            <div className="flex items-center text-sm text-gray-600">
              <MapPin className="h-4 w-4 mr-2 text-green-500" />
              <span>{booking.travellers[0].tripLocation?.name || 'Location not specified'}</span>
              <span className="ml-1 text-gray-400">({booking.travellers[0].tripLocation?.displayText})</span>
            </div>
          )}

          <div className="flex items-center text-sm text-gray-600">
            <Users className="h-4 w-4 mr-2 text-purple-500" />
            <span>{booking.count || 0} {booking.count === 1 ? 'Person' : 'People'}</span>
          </div>
        </div>

        {/* Travellers List */}
        {booking.travellers && booking.travellers.length > 0 && (
          <div className="mb-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Travellers</h4>
            <div className="space-y-1">
              {booking.travellers.map((traveller, index) => (
                <div key={traveller.id} className="flex items-center justify-between text-xs bg-gray-50 rounded-lg p-2">
                  <div className="flex items-center">
                    <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-2">
                      <span className="text-blue-600 font-medium text-xs">
                        {traveller.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <div className="font-medium text-gray-700">{traveller.name}</div>
                      <div className="text-gray-500">{getGenderText(traveller.gender)}</div>
                    </div>
                  </div>
                  {traveller.primaryUser && (
                    <span className="text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded-full">
                      Primary
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Payment Details */}
        <div className="border-t pt-3">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Payment Details</h4>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Order Amount:</span>
              <span className="font-medium">{formatAmount(booking.orderAmount || 0)}</span>
            </div>
            {booking.totalDiscount && booking.totalDiscount > 0 && (
              <div className="flex justify-between text-green-600">
                <span>Discount:</span>
                <span>-{formatAmount(booking.totalDiscount)}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-gray-600">Amount Paid:</span>
              <span className="font-medium text-green-600">{formatAmount(booking.amountPaid || 0)}</span>
            </div>
            {booking.amountRemaining && booking.amountRemaining > 0 && (
              <div className="flex justify-between text-orange-600">
                <span>Remaining:</span>
                <span>{formatAmount(booking.amountRemaining)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Booking ID and Details */}
        <div className="mt-3 pt-3 border-t">
          <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
            <span>Booking ID: {booking.id}</span>
            <span>{booking.subText}</span>
          </div>
          {booking.createdOn && (
            <div className="text-xs text-gray-400">
              Booked on {new Date(booking.createdOn).toLocaleDateString('en-IN', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="mt-4 flex space-x-2">
          {canDownloadReceipt() && (
            <>
              <button
                onClick={handleDownloadReceipt}
                className="flex-1 bg-green-600 text-white text-xs py-2 px-3 rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center"
              >
                <Download className="h-3 w-3 mr-1" />
                Download Receipt
              </button>
              <button
                onClick={handleSendReceipt}
                className="flex-1 bg-blue-600 text-white text-xs py-2 px-3 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center"
              >
                <Send className="h-3 w-3 mr-1" />
                Send Receipt
              </button>
            </>
          )}
          {booking.travellers && booking.travellers[0]?.tripLocation?.url && (
            <button
              onClick={() => window.open(booking.travellers![0].tripLocation.url, '_blank')}
              className="flex-1 bg-blue-600 text-white text-xs py-2 px-3 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center"
            >
              <MapPin className="h-3 w-3 mr-1" />
              View Location
            </button>
          )}
          {booking.canEdit && (
            <button className="flex-1 bg-gray-600 text-white text-xs py-2 px-3 rounded-lg hover:bg-gray-700 transition-colors">
              Edit Booking
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
